import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { io } from 'socket.io-client';
import { 
  Play, 
  DollarSign, 
  Users, 
  Clock, 
  TrendingUp,
  Trophy,
  BarChart3,
  Settings,
  Zap
} from 'lucide-react';
import CockFight3D from '../components/CockFight3D';
import Coin from '../components/Coin';
import BettersListCard from '../components/BettersListCard';
// Animation CSS pour le message de victoire/défaite
if (typeof document !== 'undefined' && !document.getElementById('fade-message-style')) {
  const fadeMessageStyle = `
    .fade-message {
      opacity: 0;
      transition: opacity 0.5s;
      position: fixed;
      top: 30px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 50;
      font-size: 1.25rem;
      font-weight: bold;
      padding: 1rem 2rem;
      border-radius: 0.5rem;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      pointer-events: none;
    }
    .fade-message.show {
      opacity: 1;
    }
  `;
  const style = document.createElement('style');
  style.id = 'fade-message-style';
  style.innerHTML = fadeMessageStyle;
  document.head.appendChild(style);
}

const WAIT_COUNTDOWN = 4;

const Game = () => {
  const { user, updateBalance } = useAuthStore();
  const [socket, setSocket] = useState(null);
  const [gameState, setGameState] = useState({
    status: 'waiting', // waiting, active, finished
    multiplier: 1.0,
    timeElapsed: 0,
    players: [],
    gameId: null,
    countdown: WAIT_COUNTDOWN 
  });
  
  // États pour les paris
  const [betAmount, setBetAmount] = useState('10');
  const [selectedCock, setSelectedCock] = useState('cock1'); // cock1 ou cock2
  const [myBet, setMyBet] = useState(null);
  const [canCashout, setCanCashout] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  // États pour l'auto-cashout
  // Message de victoire/défaite
  const [resultMessage, setResultMessage] = useState(null); // { type: 'win'|'lose', text: string }
  const [autoCashoutEnabled, setAutoCashoutEnabled] = useState(false);
  const [autoCashoutMultiplier, setAutoCashoutMultiplier] = useState(2.0);
  // Valeur du multiplicateur où on a encaissé (null si pas encaissé)
  const [cashoutAt, setCashoutAt] = useState(null);
  
  // États pour les paris en attente
  const [pendingBet, setPendingBet] = useState(null);
  const [canPlaceBet, setCanPlaceBet] = useState(true);
  const [betType, setBetType] = useState(null); // 'cock', 'multiplier', or null
  const [nextRoundBetType, setNextRoundBetType] = useState(null);
  
  const multiplierRef = useRef(null);
  const animationRef = useRef(null);
  const autoCashoutEnabledRef = useRef(autoCashoutEnabled);
  const autoCashoutMultiplierRef = useRef(autoCashoutMultiplier);
  const myBetRef = useRef(myBet);
  const pendingBetRef = useRef(pendingBet);
  const lastAppliedGameIdRef = useRef(null);
  const nextRoundBetTypeRef = useRef(nextRoundBetType);
  const messageTimeoutRef = useRef(null);

  // Contrôle d'affichage temporaire de la carte joueurs
  const [showBettersCard, setShowBettersCard] = useState(false);

  // ✅ Fonction utilitaire pour nettoyer l'état de pari
  const resetBetState = () => {
    setMyBet(null);
    setCanCashout(false);
    setCashoutAt(null);
    setResultMessage(null);
  };

  // Montants prédéfinis
  const presetAmounts = ['MIN', 10, 25, 50, 100, 'MAX'];

  // Sync refs with latest state values used inside socket event handlers
  useEffect(() => { autoCashoutEnabledRef.current = autoCashoutEnabled; }, [autoCashoutEnabled]);
  useEffect(() => { autoCashoutMultiplierRef.current = autoCashoutMultiplier; }, [autoCashoutMultiplier]);
  useEffect(() => { myBetRef.current = myBet; }, [myBet]);
  useEffect(() => { pendingBetRef.current = pendingBet; }, [pendingBet]);
  useEffect(() => { nextRoundBetTypeRef.current = nextRoundBetType; }, [nextRoundBetType]);

  useEffect(() => {
    if (!user) return;

    // Connexion WebSocket
    const newSocket = io('https://kokbatay.com/api');
    setSocket(newSocket);

    // Événements de connexion
    newSocket.on('connect', () => {
      console.log('🔌 Connecté au serveur de jeu');
      setIsConnected(true);
      setError(null);
      
      // Rejoindre le jeu
      newSocket.emit('join-game', {
        userId: user.id,
        username: user.username
      });
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Déconnecté du serveur de jeu');
      setIsConnected(false);
    });

    // Événements du jeu
    newSocket.on('game-state', (data) => {
      const sanitizedPlayers = data && data.players ? (data.status === 'active' ? data.players : data.players.map(p => ({ ...p, bet: null }))) : [];
      setGameState({
        ...data,
        players: sanitizedPlayers
      });
    });

    newSocket.on('new-game', (data) => {
      console.log('🎮 Nouvelle partie:', data);
      setGameState(prev => ({
        ...prev,
        status: 'waiting',
        multiplier: 1.0,
        timeElapsed: 0,
        gameId: data.gameId,
        countdown: WAIT_COUNTDOWN,
        players: []
      }));
      setMyBet(null);
      setCanCashout(false);
      setCanPlaceBet(true);
      setBetType(null);
      
      // Si un pari est en attente, il sera posé automatiquement quand l'état passera à "waiting" (voir watcher)
    });

    newSocket.on('game-started', (data) => {
      console.log('🎮 Partie démarrée:', data);
      setGameState(prev => ({
        ...prev,
        status: 'active',
        timeElapsed: 0,
        countdown: 0
      }));
      setCanCashout(true);
      setCanPlaceBet(false);
    });

    newSocket.on('multiplier-update', (data) => {
      setGameState(prev => ({
        ...prev,
        multiplier: data.multiplier,
        timeElapsed: data.timeElapsed
      }));
      
    // Vérifier l'auto-cashout (uniquement pour les paris de type 'multiplier')
    // Utiliser les refs pour éviter les closures obsolètes attachées lors de la création du socket
    if (
      autoCashoutEnabledRef.current &&
      myBetRef.current && myBetRef.current.type === 'multiplier' &&
      data.multiplier >= autoCashoutMultiplierRef.current
    ) {
      handleCashout({ auto: true });
    }
    });

    newSocket.on('game-ended', (data) => {
      console.log('🎮 Partie terminée:', data);
      setGameState(prev => ({
        ...prev,
        status: 'finished',
        multiplier: data.finalMultiplier,
        players: prev.players.map(p => ({ ...p, bet: null }))
      }));
      setCanCashout(false);
      setCanPlaceBet(true);
      
      // Afficher le résultat du combat (utilise un snapshot fiable de votre pari)
      const betSnapshot = myBetRef.current;
      if (data.winningCock) {
        if (betSnapshot && betSnapshot.type === 'cock') {
          if (betSnapshot.selectedCock === data.winningCock) {
            setResultMessage({ type: 'win', text: '🎉 Vous avez gagné !' });
          } else {
            setResultMessage({ type: 'lose', text: '😔 Vous avez perdu...' });
          }
          setTimeout(() => setResultMessage(null), 2500);
        }
      }
      
      // Réinitialiser le pari courant après avoir affiché le message
      setMyBet(null);
      
      // Ajouter à l'historique
      setGameHistory(prev => [
        {
          multiplier: data.finalMultiplier,
          reason: data.reason,
          timestamp: Date.now(),
          winningCock: data.winningCock
        },
        ...prev.slice(0, 19) // Garder seulement les 20 dernières
      ]);
    });

    newSocket.on('bet-placed', (data) => {
      console.log('🎲 Pari placé:', data);
    });

    newSocket.on('bet-confirmed', (data) => {
      console.log('✅ Pari confirmé:', data);
      setMyBet(data);
      updateBalance(data.newBalance);
      setCanPlaceBet(false);
      setNextRoundBetType(null);
      if (data.type === 'multiplier') {
        setResultMessage({ type: 'info', text: 'Vous avez misé sur le temps de jeu !' });
        setTimeout(() => setResultMessage(null), 2500);
      } else if (data.type === 'cock') {
        setResultMessage({ type: 'info', text: `Vous avez mis�� sur le coq ${data.selectedCock === 'cock1' ? '1' : '2'} !` });
        setTimeout(() => setResultMessage(null), 2500);
      }
    });

    newSocket.on('players-updated', (data) => {
      console.log('👥 Liste des joueurs mise à jour:', data);
      setGameState(prev => ({
        ...prev,
        players: data.players
      }));
    });

    // Mise à jour du solde émise par le serveur (gains crédités)
    newSocket.on('balance-updated', (data) => {
      console.log('💳 Solde mis à jour:', data);
      if (data && data.userId === user.id && typeof data.newBalance === 'number') {
        updateBalance(data.newBalance);
      }
    });

    newSocket.on('cashout', (data) => {
      console.log('💰 Encaissement:', data);
    });

    newSocket.on('cashout-confirmed', (data) => {
      console.log('✅ Encaissement confirmé:', data);
      setMyBet(null);
      setCanCashout(false);
      if (!data || data.status !== 'pending') {
        if (typeof data?.newBalance === 'number') {
          updateBalance(data.newBalance);
        }
      }
  showMessage({ type: 'win', text: <Coin amount={data.winnings.toFixed(2)} symbol="HTG" /> }, 2500);
    });

    newSocket.on('error', (data) => {
      console.error('❌ Erreur:', data);
      setError(data.message);
    });

    return () => {
      newSocket.disconnect();
    };
  }, [user]);

  // Animation du multiplicateur
  useEffect(() => {
    if (gameState.status === 'active' && multiplierRef.current) {
      const animate = () => {
        if (multiplierRef.current) {
          multiplierRef.current.style.transform = `scale(${1 + (gameState.multiplier - 1) * 0.1})`;
        }
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameState.multiplier, gameState.status]);

  // Compte à rebours pendant la phase d'attente
  useEffect(() => {
    let countdownInterval;
    
    if (gameState.status === 'waiting' && gameState.countdown > 0) {
      countdownInterval = setInterval(() => {
        setGameState(prev => ({
          ...prev,
          countdown: Math.max(0, prev.countdown - 1)
        }));
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [gameState.status, gameState.countdown]);

  // Cleanup pour le timer de message à la destruction du composant
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Appliquer automatiquement la mise en attente quand la nouvelle manche passe à 'waiting'
  useEffect(() => {
    if (
      gameState.status === 'waiting' &&
      pendingBetRef.current &&
      gameState.gameId &&
      lastAppliedGameIdRef.current !== gameState.gameId
    ) {
      console.log('🔁 Application automatique du pari en attente pour la manche:', gameState.gameId);
      placeBet(pendingBetRef.current);
      setPendingBet(null);
      lastAppliedGameIdRef.current = gameState.gameId;
    }
  }, [gameState.status, gameState.gameId]);

  // Affiche la carte pendant les phases waiting et active
  useEffect(() => {
    setShowBettersCard(gameState.status === 'active' || gameState.status === 'waiting');
  }, [gameState.status]);

  const placeBet = (betData) => {
    if (!socket || !user || !betData.amount) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    if (betData.type === 'cock' && !betData.selectedCock) {
      setError('Veuillez sélectionner un coq');
      return;
    }

    const amount = parseFloat(betData.amount);
    if (isNaN(amount) || amount < 1 || amount > 10000) {
      setError(<>Le montant doit être entre <Coin amount={1} symbol="HTG" /> et <Coin amount={10000} symbol="HTG" /></>);
      return;
    }

    if (amount > user.balance) {
      setError('Solde insuffisant');
      return;
    }

    // Empêcher un pari sur l'autre jeu s'il y a déjà un type de pari programmé pour la prochaine manche
    if (nextRoundBetTypeRef.current && nextRoundBetTypeRef.current !== betData.type) {
      setError('Vous avez déjà un pari programmé pour l\'autre jeu');
      return;
    }

    // Empêcher un pari sur l'autre jeu s'il y a déjà un pari en attente
    if (pendingBetRef.current && pendingBetRef.current.type && pendingBetRef.current.type !== betData.type) {
      setError('Vous avez déjà un pari en attente pour l\'autre jeu');
      return;
    }

    // Si la partie est en cours, sauvegarder le pari pour la prochaine partie
    if (gameState.status === 'active' || gameState.status === 'finished') {
      setPendingBet(betData);
      setNextRoundBetType(betData.type);
      setError(null);
      return;
    }

    // Si on peut parier maintenant (nouvelle partie)
    if (gameState.status === 'waiting') {
      socket.emit('place-bet', {
        ...betData,
        userId: user.id,
        amount: amount,
        autoCashoutEnabled: autoCashoutEnabled,
        autoCashoutMultiplier: autoCashoutMultiplier, // Envoyer la valeur du multiplicateur choisie par l'utilisateur
      });
    }
  };

  const handlePlaceCockBet = () => {
    if (!selectedCock) {
      setError('Veuillez sélectionner un coq');
      return;
    }
    setBetType('cock');
    placeBet({
      amount: betAmount,
      selectedCock: selectedCock,
      type: 'cock'
    });
  };

  const handlePlaceMultiplierBet = () => {
    console.log('handlePlaceMultiplierBet called - betAmount=', betAmount, 'autoCashoutEnabled=', autoCashoutEnabled);
    setBetType('multiplier');
    placeBet({
      amount: betAmount,
      type: 'multiplier',
      selectedCock: null // Assurer que la propriété existe
    });
  };

  const showMessage = (message, duration = 2500) => {
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    setResultMessage(message);
    messageTimeoutRef.current = setTimeout(() => setResultMessage(null), duration);
  };

  const handleCashout = (opts = { auto: false }) => {
    if (!socket || !user || !myBet || !canCashout) {
      setError('Impossible d\'encaisser maintenant');
      return;
    }

    // Empêcher double encaissement
    if (cashoutAt) return;

    const currentMult = gameState.multiplier || 1;
    setCashoutAt(currentMult);

    // Calcul du gain estimé
    let estWinnings;
    if (autoCashoutEnabled && myBet.type === 'multiplier' && myBet.autoCashoutMultiplier) {
      estWinnings = (parseFloat(myBet.amount) || 0) * myBet.autoCashoutMultiplier;
    } else {
      estWinnings = (parseFloat(myBet.amount) || 0) * (parseFloat(gameState.multiplier) || 1);
    }

    // Message spécifique pour auto-cashout: afficher montant encaissé
    if (opts.auto) {
      showMessage({ type: 'win', text: <>Vous avez encaissé <Coin amount={estWinnings.toFixed(2)} symbol="HTG" /></> }, 2500);
    } else {
      showMessage({ type: 'win', text: <>Vous avez encaissé <Coin amount={estWinnings.toFixed(2)} symbol="HTG" /></> }, 2500);
    }

    setCanCashout(false);

    // Ne pas effacer immédiatement le message ni cashoutAt — laisser le message s'afficher.
    const duration = opts.auto ? 2500 : 2500;
    setTimeout(() => {
      resetBetState();
    }, duration + 150);

    socket.emit('cashout', { userId: user.id });
  };

  const setPresetAmount = (amount) => {
    setBetAmount(amount.toString());
  };

  const adjustBetAmount = (increment) => {
    const current = parseFloat(betAmount) || 0;
    const newAmount = Math.max(1, Math.min(10000, current + increment));
    setBetAmount(newAmount.toString());
  };

  const adjustAutoCashout = (increment) => {
    const current = parseFloat(autoCashoutMultiplier) || 2.0;
    const newMultiplier = Math.max(1.1, Math.min(100, current + increment));
    setAutoCashoutMultiplier(newMultiplier);
  };

  const getStatusColor = () => {
    switch (gameState.status) {
      case 'waiting': return 'bg-yellow-500';
      case 'active': return 'bg-green-500';
      case 'finished': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (gameState.status) {
      case 'waiting': return 'En attente';
      case 'active': return 'En cours';
      case 'finished': return 'Terminé';
      default: return 'Inconnu';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Veuillez vous connecter pour jouer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Statut de connexion */}
        <div className="mb-6">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            isConnected 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${
              isConnected ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            {isConnected ? 'Connecté' : 'Déconnecté'}
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mb-0">
          {/* Historique des parties en haut */}
          {gameHistory.length > 0 && (
            <div className="lg:col-span-3 mb-4 bg-white rounded shadow-md p-2 text-xs">
              <h3 className="text-xs font-semibold text-gray-900 mb-1">Historique récent</h3>
              <div id="history-scroll" className="overflow-x-hidden relative w-full">
                <div className="flex flex-nowrap animate-scroll space-x-2">
                  {gameHistory.slice(0, 15).map((game, index) => (
                    <div
                      key={`main-${index}`}
                      className={`flex-shrink-0 flex items-center space-x-1 px-2 py-1 rounded shadow ${
                        game.multiplier >= 2 ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="text-[10px] text-gray-500">
                        {new Date(game.timestamp).toLocaleTimeString()}
                      </div>
                      <div
                        className={`text-xs font-bold ${
                          game.multiplier >= 2 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {game.multiplier.toFixed(2)}x
                      </div>
                      {game.winningCock && (
                        <div className="text-[10px] text-gray-600">
                          🏆 Coq {game.winningCock === 'cock1' ? '1' : '2'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Zone de jeu principale élargie */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-6">
              {/* Statut du jeu */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getStatusColor()}`}>
                    {getStatusText()}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="w-4 h-4 mr-1" />
                    <span>{gameState.players.length} joueurs</span>
                  </div>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{gameState.gameId ? `#${gameState.gameId.slice(-6)}` : 'N/A'}</span>
                </div>
              </div>

              {/* Messages au-dessus du conteneur 3D */}
              {myBet && gameState.status === 'active' && canCashout && (
                <div className="mt-6 text-center">
                  <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <p className="text-green-800 font-medium text-xs sm:text-base">
                      Votre pari: <Coin amount={myBet.amount} symbol="HTG" /> {myBet.type === 'cock' ? (<>
                        sur Coq {myBet.selectedCock === 'cock1' ? '1' : '2'}
                      </>) : 'sur le temps de jeu'}
                    </p>
                    {myBet.type === 'multiplier' && (
                      <p className="text-green-600 text-xs sm:text-base">
                        Gains potentiels: <Coin amount={(myBet.amount * gameState.multiplier).toFixed(2)} symbol="HTG" />
                      </p>
                    )}
                  </div>
                </div>
              )}

              
              {gameState.status === 'finished' && (
                <div className="mt-6 text-center">
                  <div className={`p-4 rounded-lg ${
                    gameState.multiplier >= 2 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    <p className="text-lg font-medium text-xs sm:text-base">
                      Partie terminée à {gameState.multiplier.toFixed(2)}x
                    </p>
                  </div>
                </div>
              )}

              {pendingBet && (
                <div className="mt-6 text-center">
                  <div className="p-4 bg-blue-50 rounded-lg text-blue-800">
                    <p className="text-lg font-medium text-xs sm:text-base">
                      ⏳ Pari en attente pour la prochaine partie
                    </p>
                    <p className="text-sm text-xs sm:text-base">
                      <Coin amount={pendingBet.amount} symbol="HTG" /> {pendingBet.type === 'cock' ? (<>
                        sur Coq {pendingBet.selectedCock === 'cock1' ? '1' : '2'}
                      </>) : 'sur le temps de jeu'}
                    </p>
                  </div>
                </div>
              )}

              {/* Liste des joueurs qui ont parié - nouvelle carte */}
              {showBettersCard && (
                <div className="overflow-y-auto max-h-56 px-4 pb-3 betters-scroll">
                  <BettersListCard bets={gameState.players.filter(p => p.bet)} />
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mb-0">
                <div className="lg:col-span-2">
                  {/* Combat 3D élargi */}
                  <div className="relative h-[px] md:h-[500px]">
                    <CockFight3D 
                      gameState={gameState}
                      myBet={myBet}
                      onCockWin={(winningCock) => {
                        console.log(`🏆 Coq ${winningCock === 'cock1' ? '1' : '2'} a gagné!`);
                      }}
                    />
                    {gameState.status === 'waiting' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-40">
                        <div className="flex flex-col items-center w-full max-w-xs mx-auto p-4">
                          <svg className="animate-spin h-8 w-8 text-orange-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                          </svg>
                          <p className="text-sm sm:text-base font-semibold text-white mb-2 text-center">En attente de la prochaine partie</p>
                          {gameState.countdown > 0 && (
                            <>
                              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden mb-2">
                                <div
                                  className="h-full bg-orange-400 transition-[width] duration-1000 ease-linear"
                                  style={{ width: `${(gameState.countdown / WAIT_COUNTDOWN) * 100}%` }}
                                />
                              </div>
                              <div className="text-center text-lg sm:text-2xl font-bold tabular-nums text-orange-300">
                                {gameState.countdown}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="lg:ml-[2px]">
                  {/* Interface de paris avancée */}
                  <div className="bg-gray-800 rounded-lg p-3 text-white text-xs">
                    <div className="grid grid-cols-1 gap-6">
                      {/* Section Montant du Pari */}
                      <div>
                        <h3 className="text-xs font-semibold mb-1">Montant du Pari</h3>
                        
                        {/* Contrôles MIN/MAX */}
                        <div className="flex items-center mb-4">
                          <button
                            onClick={() => adjustBetAmount(-1)}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-l text-base font-bold"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            value={betAmount}
                            onChange={(e) => setBetAmount(e.target.value)}
                            className="flex-1 bg-gray-700 text-white text-center py-1 px-2 border-0 focus:ring-2 focus:ring-orange-500 no-spinner text-base"
                            min="1"
                            max="10000"
                            style={{ MozAppearance: 'textfield' }}
                          />
                          <button
                            onClick={() => adjustBetAmount(1)}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-r text-base font-bold"
                          >
                            +
                          </button>
                        </div>

                        {/* Boutons de montants prédéfinis */}
                        <div className="grid grid-cols-2 gap-1">
                          {presetAmounts.map((amount) => {
                            let display = amount;
                            let value = amount;
                            if (amount === 'MIN') {
                              display = 'MIN';
                              value = 5;
                            } else if (amount === 'MAX') {
                              display = 'MAX';
                              value = 500;
                            }
                            let colorClass = '';
                            if (amount === 'MIN') {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-red-800 text-white'
                                : 'bg-red-700 text-white hover:bg-red-900';
                            } else if (amount === 'MAX') {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-green-800 text-white'
                                : 'bg-green-700 text-white hover:bg-green-900';
                            } else {
                              colorClass = parseFloat(betAmount) === value
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-700 hover:bg-gray-600 text-white';
                            }
                            return (
                              <button
                                key={amount}
                                onClick={() => setPresetAmount(value)}
                                className={`p-1 rounded text-xs font-medium transition-colors ${colorClass}`}
                              >
                                {display}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Section Auto Cashout */}
                      <div className={`${(betType === 'cock' || (pendingBet && pendingBet.type === 'cock') || nextRoundBetType === 'cock') ? 'opacity-50 pointer-events-none' : ''}`}>
                        <div className="flex items-center mb-4">
                          <h3 className="text-xs font-semibold mr-1 mb-0">Auto Cashout</h3>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); console.log('🔔 autoCashout toggle clicked ->', !autoCashoutEnabled); setAutoCashoutEnabled(!autoCashoutEnabled); }}
                            disabled={betType === 'cock'}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              autoCashoutEnabled ? 'bg-orange-500' : 'bg-gray-600'
                            }`}
                            aria-label="Activer/Désactiver Auto Cashout"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                autoCashoutEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="flex items-center">
                          <button
                            onClick={() => adjustAutoCashout(-0.1)}
                            disabled={betType === 'cock'}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-l text-xs"
                          >
                            -
                          </button>
                          <div className="flex-1 bg-gray-700 text-center py-1 px-2 text-xs">
                            {autoCashoutMultiplier.toFixed(1)}x
                          </div>
                          <button
                            onClick={() => adjustAutoCashout(0.1)}
                            disabled={betType === 'cock'}
                            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded-r text-xs"
                          >
                            +
                          </button>
                        </div>
                        {/* Bouton Retirer ma mise sous Auto Cashout */}
                        {/* <div className="mt-4">
                         <button
                              onClick={handleCashout}
                              disabled={autoCashoutEnabled || gameState.status === 'finished'}
                              className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-colors"
                            >
                              {autoCashoutEnabled
                                ? "Retrait indisponible (Auto Cashout actif)"
                                : gameState.status === 'finished'
                                  ? "Combat terminé - Retrait indisponible"
                                  : "Retirer ma mise"}
                          </button>
                        </div> */}
                         {/* Bouton dynamique Parier/Encaisser */}
                      <div className="mb-3">
                        {myBet && myBet.type === 'multiplier' && gameState.status === 'active' ? (
                          <button
                            onClick={() => handleCashout({ auto: false })}
                            disabled={!canCashout}
                            className="w-full bg-red-700 hover:bg-red-800 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-3 rounded text-[11px] transition-colors"
                          >
                            Encaisser ma mise
                          </button>
                        ) : (
                          <button
                            onClick={handlePlaceMultiplierBet}
                            disabled={betType === 'cock' || (pendingBet && pendingBet.type === 'cock') || nextRoundBetType === 'cock'}
                            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-3 rounded text-[11px] transition-colors"
                          >
                            Miser sur le temps de jeu
                          </button>
                        )}
                      </div>
                      </div>

                      {/* Section Sélection Coq et Bouton Pari */}
                      <div className={`${(betType === 'multiplier' || (pendingBet && pendingBet.type === 'multiplier') || nextRoundBetType === 'multiplier') ? 'opacity-50 pointer-events-none' : ''}`}>
                        <h3 className="text-xs font-semibold mb-1">Sélection Coq</h3>
                        
                        <div className="grid grid-cols-2 gap-1 mb-2">
                          {['cock1', 'cock2'].map((cock) => {
                            
                            return (
                              <div key={cock} className="flex flex-col items-center">
                                                                <button
                                  onClick={() => setSelectedCock(cock)}
                                  disabled={betType === 'multiplier'}
                                  className={`p-1 border rounded text-center transition-colors text-xs ${
                                    selectedCock === cock
                                      ? 'border-orange-500 bg-orange-500 text-white'
                                      : 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white'
                                  }`}
                                >
                                  <div className="text-xs mb-0.5">🐓</div>
                                  <div className="text-[10px]">Coq {cock === 'cock1' ? '1' : '2'}</div>
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <button
                          onClick={handlePlaceCockBet}
                          disabled={!selectedCock || !betAmount || betType === 'multiplier' || (pendingBet && pendingBet.type === 'multiplier') || nextRoundBetType === 'multiplier'}
                          className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-3 rounded text-[11px] transition-colors"
                        >
                          {pendingBet && pendingBet.type === 'cock' ? 'PARI EN ATTENTE' : 'PARIER'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Message victoire/défaite animé */}
              {resultMessage && (
                <div
                  className={`fade-message show ${
                    resultMessage.type === 'win'
                      ? 'bg-green-600 text-white'
                      : resultMessage.type === 'lose'
                      ? 'bg-red-700 text-white'
                      : 'bg-blue-600 text-white' // Cas pour 'info'
                  }`}
                  style={{ animation: 'fadeInOut 2.5s' }}
                >
                  {resultMessage.text}
                </div>
              )}
                            {/* Panneau de mise déplacé à droite du conteneur 3D (et empilé en responsive) */}
            </div>
          </div>

          {/* Sidebar */}
        </div>
      </div>
    </div>
  );
};

export default Game; 
// un commentaire